import { useContext } from 'react';
import { WrappedFieldProps } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import styled, { ThemeContext } from 'styled-components';

import FormError from 'inquiry/components/FormError';
import MaterialTextArea from 'inquiry/components/MaterialTextArea';
import TextArea from 'inquiry/components/TextArea';
import { containerSSTStyles, labelSSTStyles, materialContainerStyles } from 'inquiry/mixins/form';
import { InputStyles } from 'lib/interfaces/input-styles';
import { InputTextBaseStyles } from 'lib/interfaces/ui-components';

const OutlinedContainer = styled('div')<{ styles?: InputTextBaseStyles }>`
  ${({ styles }) => containerSSTStyles(styles)}
  ${({ styles }) => labelSSTStyles(styles)}
`;

const MaterialContainer = styled('div')`
  ${materialContainerStyles}
`;

export interface ThemedFormTextAreaProps {
  autoFocus?: boolean;
  id: string;
  label?: string;
  className?: string;
  placeholder?: string;
  'data-test'?: string;
  rows?: number;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  styles?: InputTextBaseStyles;
}

const ThemedFormTextArea = (props: WrappedFieldProps & ThemedFormTextAreaProps) => {
  const {
    id,
    className,
    input,
    meta: { error, submitFailed },
    autoFocus,
    label,
    placeholder,
    styles,
    'data-test': dataTest = 'form',
    ...passthroughProps
  } = props;

  const { inputStyle } = useContext(ThemeContext);

  const hasError = !!(error && submitFailed);

  switch (inputStyle) {
    case InputStyles.Material:
      return (
        <MaterialContainer className={className}>
          {/* Note we fall back to the place holder for material if there is no label */}
          <MaterialTextArea
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            labelText={label || placeholder}
            {...input}
            {...passthroughProps}
            autoFocus={autoFocus}
            hasError={hasError}
            aria-invalid={hasError}
            aria-errormessage={`${id}--error`}
            aria-describedby={`${id}--error`}
            id={id}
            data-test={dataTest}
            styles={styles}
          />
          <FormError
            id={`${id}--error`}
            data-test={`${dataTest}-error`}
            hasError={hasError}
            errorMessage={error}
            styles={styles}
          />
        </MaterialContainer>
      );
    case InputStyles.Outlined:
    default:
      return (
        <OutlinedContainer className={className} styles={styles}>
          {label && (
            <Markdown<'label'> wrapper='label' wrapperProps={{ htmlFor: id }}>
              {label}
            </Markdown>
          )}
          <TextArea
            {...input}
            {...passthroughProps}
            autoFocus={autoFocus}
            hasError={hasError}
            aria-invalid={hasError}
            aria-errormessage={`${id}--error`}
            aria-describedby={`${id}--error`}
            id={id}
            placeholder={placeholder}
            data-test={dataTest}
            styles={styles}
          />
          <FormError
            id={`${id}--error`}
            data-test={`${dataTest}-error`}
            hasError={hasError}
            errorMessage={error}
            styles={styles}
          />
        </OutlinedContainer>
      );
  }
};

export default ThemedFormTextArea;
